import type { MediaItem } from '../types';
import type { MotionLevel, MotionPlan, Pace, ParallaxPlan, ParallaxPose, PlayerPreferences, Pose, Shot, TransitionKind, TransitionSpec } from './types';

/**
 * The film's edit decision list: turns an episode's ordered memories into
 * shots with a hold time, an entrance transition and a Ken Burns move.
 *
 * Deterministic on purpose. Every "random" choice comes from a hash of the
 * media id, so the same story always plays the same way, re-renders never
 * reshuffle it, and nothing is claimed about what a photo contains.
 */

/** Photo hold ranges in ms. Videos always use their own length. */
export const PACE_RANGES: Record<Pace, [number, number]> = {
  relaxed: [6000, 8000],
  cinematic: [4000, 5500],
  dynamic: [2800, 3600],
};

/** Base transition length per pace; individual kinds scale it. */
const TRANSITION_BASE: Record<Pace, number> = { relaxed: 1500, cinematic: 1100, dynamic: 800 };
const KIND_SCALE: Record<TransitionKind, number> = {
  dissolve: 1,
  'fade-black': 1.4,
  push: 1,
  zoom: 1,
  blur: 1.1,
  cut: 0,
};

/** A transition never takes more than this share of the shorter shot. */
const MAX_TRANSITION_SHARE = 0.4;
/** Quick dissolve used for manual navigation, so controls feel immediate. */
export const MANUAL_TRANSITION: TransitionSpec = { kind: 'dissolve', ms: 280 };
/** Gap between capture times that marks a new scene. */
const SCENE_GAP_MS = 3 * 60 * 60 * 1000;

/** Capture-date sources that come from the file itself, not a fallback. */
const TRUSTED_DATE_SOURCES = new Set([
  'EXIF_DT_ORIGINAL',
  'EXIF_CREATE_DATE',
  'EXIF_OFFSET_DT_ORIGINAL',
  'VIDEO_CREATION_TIME',
]);

/** FNV-1a: a stable 0..1 value per (id, salt). */
export function hash01(id: string, salt = ''): number {
  let h = 0x811c9dc5;
  const s = id + salt;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0) / 0xffffffff;
}

const MOTION_AMPLITUDE: Record<MotionLevel, number> = { off: 0, subtle: 0.5, standard: 1, expressive: 1.5 };

type PresetName = 'zoom-in' | 'zoom-out' | 'pan-left' | 'pan-right' | 'pan-up' | 'pan-down' | 'drift-left' | 'drift-right';
const LANDSCAPE_PRESETS: PresetName[] = ['zoom-in', 'pan-left', 'zoom-out', 'drift-right', 'pan-right', 'zoom-in', 'drift-left', 'pan-up'];
const PORTRAIT_PRESETS: PresetName[] = ['zoom-in', 'pan-up', 'zoom-out', 'pan-down', 'zoom-in'];

/**
 * Ken Burns poses. Translation is always a fraction of the margin the zoom
 * creates ((scale − 1) / 2), so a cover-framed photo never reveals an edge.
 * Moves stay centre-weighted and small: no face detection is claimed, so
 * nothing dramatic is cropped.
 */
function posesFor(name: PresetName, amp: number): { from: Pose; to: Pose } {
  const s = 1 + 0.06 * amp + 0.02;
  const margin = ((s - 1) / 2) * 100; // % of the element
  const pan = margin * 0.75;
  const zero = { scale: 1, x: 0, y: 0 };
  switch (name) {
    case 'zoom-in':
      return { from: zero, to: { scale: s, x: 0, y: 0 } };
    case 'zoom-out':
      return { from: { scale: s, x: 0, y: 0 }, to: { scale: 1.005, x: 0, y: 0 } };
    case 'pan-left':
      return { from: { scale: s, x: pan, y: 0 }, to: { scale: s, x: -pan, y: 0 } };
    case 'pan-right':
      return { from: { scale: s, x: -pan, y: 0 }, to: { scale: s, x: pan, y: 0 } };
    case 'pan-up':
      return { from: { scale: s, x: 0, y: pan }, to: { scale: s, x: 0, y: -pan } };
    case 'pan-down':
      return { from: { scale: s, x: 0, y: -pan }, to: { scale: s, x: 0, y: pan } };
    case 'drift-left':
      return { from: zero, to: { scale: s, x: -margin * 0.6, y: -margin * 0.2 } };
    case 'drift-right':
      return { from: zero, to: { scale: s, x: margin * 0.6, y: -margin * 0.2 } };
  }
}

/** Near-object travel for the 3D effect, per unit of motion amplitude. */
const PARALLAX_SHIFT = 0.024;
const PARALLAX_DOLLY = 0.07;

/**
 * The 3D move follows the Ken Burns direction, so the zoom/pan and the depth
 * travel agree: panning left carries near things left faster than far ones.
 */
function parallaxFor(name: PresetName, amp: number, flip: boolean): { from: ParallaxPose; to: ParallaxPose } {
  const s = PARALLAX_SHIFT * amp;
  const d = PARALLAX_DOLLY * amp;
  const side = flip ? -1 : 1;
  const p = (x: number, y: number, dolly: number): ParallaxPose => ({ x, y, dolly });
  switch (name) {
    case 'zoom-in':
      return { from: p(-s * 0.4 * side, 0, 0), to: p(s * 0.4 * side, 0, d) };
    case 'zoom-out':
      return { from: p(s * 0.4 * side, 0, d), to: p(-s * 0.4 * side, 0, 0) };
    case 'pan-left':
      return { from: p(s, 0, d * 0.3), to: p(-s, 0, d * 0.3) };
    case 'pan-right':
      return { from: p(-s, 0, d * 0.3), to: p(s, 0, d * 0.3) };
    case 'pan-up':
      return { from: p(0, s, d * 0.3), to: p(0, -s, d * 0.3) };
    case 'pan-down':
      return { from: p(0, -s, d * 0.3), to: p(0, s, d * 0.3) };
    case 'drift-left':
      return { from: p(s * 0.6, 0, 0), to: p(-s * 0.6, -s * 0.2, d * 0.6) };
    case 'drift-right':
      return { from: p(-s * 0.6, 0, 0), to: p(s * 0.6, -s * 0.2, d * 0.6) };
  }
}

function capturedMs(item: MediaItem): number | null {
  if (!TRUSTED_DATE_SOURCES.has(item.capturedAtSource)) return null;
  const t = new Date(item.capturedAt).getTime();
  return Number.isFinite(t) ? t : null;
}

function isSceneChange(prev: MediaItem, next: MediaItem): boolean {
  const a = capturedMs(prev);
  const b = capturedMs(next);
  if (a !== null && b !== null && Math.abs(b - a) > SCENE_GAP_MS) return true;
  return Boolean(prev.locationName && next.locationName && prev.locationName !== next.locationName);
}

function isPortrait(item: MediaItem): boolean {
  return Boolean(item.width && item.height && item.height > item.width * 1.05);
}

function videoMs(item: MediaItem): number {
  // A trimmed video plays only its kept parts.
  return Math.max(1000, (item.trim?.keptSeconds ?? item.duration ?? 5) * 1000);
}

export interface ShotOptions {
  prefs: PlayerPreferences;
  /** Short plain dissolves, no movement anywhere. */
  reducedMotion: boolean;
  /** How photos move, already resolved from 'ask'. */
  photoMotion: 'still' | 'motion' | 'depth';
  /**
   * Memories with a story line (subtitle and/or narration), by media id.
   * `audioMs` is the narration's length in the language being played (0 when
   * the voice is off). The memory then holds for a lead-in, the line and a
   * pause after it; a video shorter than that holds its last frame.
   */
  lines?: Map<string, { audioMs: number; text: string }>;
}

/** Shortest hold for a memory with a subtitle, so it can be read. */
export const READING_MIN_MS = 5500;
/** The picture settles before the voice starts (scaled per memory, see lineTiming). */
const LEAD_BASE: Record<Pace, number> = { relaxed: 1000, cinematic: 800, dynamic: 550 };
/** The breath after a line before the next memory comes in. */
const TAIL_BASE: Record<Pace, number> = { relaxed: 1700, cinematic: 1300, dynamic: 1000 };

/**
 * Timing of one narrated memory: a lead-in (the picture arrives first), the
 * line, then a pause. Both vary a little per memory (stable per id), and the
 * pause is longer after a punchline ("!" / "?" / "...") and after the last
 * line, so the edit breathes like a film instead of cutting on the last word.
 */
export function lineTiming(
  id: string,
  text: string,
  audioMs: number,
  pace: Pace,
  isLast = false,
): { leadMs: number; tailMs: number; holdMs: number } {
  if (audioMs <= 0) return { leadMs: 0, tailMs: 0, holdMs: READING_MIN_MS };
  const leadMs = Math.round(LEAD_BASE[pace] * (0.8 + 0.4 * hash01(id, 'lead')));
  const end = text.trim();
  let tailMs = TAIL_BASE[pace] * (0.8 + 0.5 * hash01(id, 'tail'));
  if (/[!?]["'”’)]*$/.test(end)) tailMs += 450;
  else if (/(\.\.\.|…)["'”’)]*$/.test(end)) tailMs += 300;
  if (isLast) tailMs += 700;
  tailMs = Math.round(tailMs);
  return { leadMs, tailMs, holdMs: Math.max(READING_MIN_MS, leadMs + audioMs + tailMs) };
}

export function buildShots(items: MediaItem[], { prefs, reducedMotion, photoMotion, lines }: ShotOptions): Shot[] {
  const [lo, hi] = PACE_RANGES[prefs.pace];
  const amp = reducedMotion || photoMotion === 'still' ? 0 : MOTION_AMPLITUDE[prefs.motion];

  // 1. Holds and scene boundaries.
  const lastLineId = [...items].reverse().find((i) => lines?.has(i.id))?.id;
  const base = items.map((item, index) => {
    const kind = item.type === 'VIDEO' ? ('video' as const) : ('photo' as const);
    // Subtle variation: no two photos linger for exactly the same time.
    let holdMs = kind === 'video' ? videoMs(item) : Math.round(lo + (hi - lo) * hash01(item.id, 'hold'));
    const line = lines?.get(item.id);
    const timing = line ? lineTiming(item.id, line.text, line.audioMs, prefs.pace, item.id === lastLineId) : null;
    if (timing) holdMs = Math.max(holdMs, timing.holdMs);
    const sceneStart = index > 0 && isSceneChange(items[index - 1], item);
    return { item, index, kind, holdMs, sceneStart, voiceDelayMs: timing?.leadMs ?? 0, voiceMs: line?.audioMs ?? 0 };
  });

  // 2. Transitions, chosen with restraint.
  const transitions: TransitionSpec[] = [];
  let lastKind: TransitionKind | null = null;
  for (const b of base) {
    let kind: TransitionKind;
    if (b.index === 0) kind = 'dissolve'; // rises out of the title card / black
    else if (reducedMotion) kind = prefs.transition === 'cut' ? 'cut' : 'dissolve';
    else if (prefs.transition !== 'auto') kind = prefs.transition;
    else {
      const prev = base[b.index - 1];
      if (b.sceneStart) kind = 'fade-black';
      else if (b.kind === 'video' || prev.kind === 'video') kind = 'dissolve';
      // An occasional gentle zoom-through, never twice in a row.
      else if (hash01(b.item.id, 'tx') < 0.16 && lastKind !== 'zoom') kind = 'zoom';
      else kind = 'dissolve';
    }
    let ms = Math.round(TRANSITION_BASE[prefs.pace] * KIND_SCALE[kind]);
    if (b.index === 0) ms = 1400;
    if (reducedMotion && kind !== 'cut') ms = Math.min(ms, 500);
    const shorter = b.index === 0 ? b.holdMs : Math.min(b.holdMs, base[b.index - 1].holdMs);
    ms = Math.min(ms, Math.round(shorter * MAX_TRANSITION_SHARE));
    transitions.push({ kind, ms });
    lastKind = kind;
  }

  // 3. Motion, varied so consecutive photos never repeat a move.
  let lastPreset: PresetName | null = null;
  return base.map((b) => {
    let motion: MotionPlan | null = null;
    let parallax: ParallaxPlan | null = null;
    if (b.kind === 'photo' && amp > 0) {
      const presets = isPortrait(b.item) ? PORTRAIT_PRESETS : LANDSCAPE_PRESETS;
      let pick = presets[Math.floor(hash01(b.item.id, 'motion') * presets.length)];
      if (pick === lastPreset) pick = presets[(presets.indexOf(pick) + 1) % presets.length];
      lastPreset = pick;
      // Keep moving through the outgoing transition so motion never stops dead.
      const outMs = transitions[b.index + 1]?.ms ?? 1500;
      const durationMs = b.holdMs + outMs;
      if (photoMotion === 'depth' && b.item.depthUrl) {
        // The depth travel carries most of the movement; keep the 2D move light.
        motion = { name: pick, ...posesFor(pick, amp * 0.5), durationMs };
        parallax = { ...parallaxFor(pick, amp, hash01(b.item.id, 'side') < 0.5), durationMs };
      } else {
        motion = { name: pick, ...posesFor(pick, amp), durationMs };
      }
    } else {
      lastPreset = null;
    }
    return {
      index: b.index,
      item: b.item,
      kind: b.kind,
      holdMs: b.holdMs,
      voiceDelayMs: b.voiceDelayMs,
      voiceMs: b.voiceMs,
      transitionIn: transitions[b.index],
      motion,
      parallax,
      sceneStart: b.sceneStart,
      trustedDate: TRUSTED_DATE_SOURCES.has(b.item.capturedAtSource),
    };
  });
}

/** Total running time of the shot list, in ms. */
export function totalRuntimeMs(shots: Shot[]): number {
  return shots.reduce((n, s) => n + s.holdMs, 0);
}
